import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TableCell,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Visibility, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';
import PaginatedTable from '../components/PaginatedTable';

const OutgoingInvoicesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadInvoices();
  }, [page, rowsPerPage, searchQuery]);

  const loadInvoices = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const mockInvoices = [
        { id: 1, invoiceNumber: 'OUT-2026-001', client: 'Atlas Construction Co', date: '2026-06-10', amount: 25000, status: 'Paid', dueDate: '2026-07-10' },
        { id: 2, invoiceNumber: 'OUT-2026-002', client: 'Meridian Logistics', date: '2026-06-12', amount: 18500, status: 'Pending', dueDate: '2026-07-12' },
        { id: 3, invoiceNumber: 'OUT-2026-003', client: 'Orchard Foods Ltd', date: '2026-06-14', amount: 32000, status: 'Paid', dueDate: '2026-07-14' },
        { id: 4, invoiceNumber: 'OUT-2026-004', client: 'Seabreeze Properties', date: '2026-06-16', amount: 21000, status: 'Overdue', dueDate: '2026-06-16' },
        { id: 5, invoiceNumber: 'OUT-2026-005', client: 'Helix Software BV', date: '2026-06-18', amount: 15700, status: 'Pending', dueDate: '2026-07-18' },
      ];
      setInvoices(mockInvoices);
      setTotalCount(mockInvoices.length);
      setLoading(false);
    }, 500);
  };

  const columns = [
    { id: 'invoiceNumber', label: t('invoice_number'), minWidth: 150 },
    { id: 'client', label: t('client'), minWidth: 200 },
    { id: 'date', label: t('date'), minWidth: 110 },
    { id: 'dueDate', label: t('due_date'), minWidth: 110 },
    { id: 'amount', label: t('amount'), minWidth: 120, align: 'right' },
    { id: 'status', label: t('status'), minWidth: 100 },
    { id: 'actions', label: t('actions'), minWidth: 100, align: 'center' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return { bg: '#E8F5E9', color: '#4CAF50' };
      case 'Pending':
        return { bg: '#FFF3E0', color: '#FF9800' };
      case 'Overdue':
        return { bg: '#FFEBEE', color: '#F44336' };
      default:
        return { bg: '#F5F5F5', color: '#666' };
    }
  };

  const renderRow = (invoice) => {
    const statusColors = getStatusColor(invoice.status);
    return (
      <TableRow hover key={invoice.id}>
        <TableCell sx={{ fontWeight: 500 }}>{invoice.invoiceNumber}</TableCell>
        <TableCell>{invoice.client}</TableCell>
        <TableCell>{invoice.date}</TableCell>
        <TableCell>{invoice.dueDate}</TableCell>
        <TableCell align="right">€{invoice.amount.toLocaleString()}</TableCell>
        <TableCell>
          <Chip
            label={invoice.status}
            size="small"
            sx={{
              backgroundColor: statusColors.bg,
              color: statusColors.color,
              fontWeight: 500,
            }}
          />
        </TableCell>
        <TableCell align="center">
          <IconButton size="small" color="primary">
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary">
            <EditIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <AccountantLayout>
      <Box>
        <Box className="accountant-page-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('outgoing_invoices')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('manage_outgoing_invoices')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
              },
            }}
          >
            {t('new_invoice')}
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder={t('search_invoices_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>

        <PaginatedTable
          columns={columns}
          data={invoices}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          emptyMessage={t('no_invoices_found')}
          renderRow={renderRow}
        />
      </Box>
    </AccountantLayout>
  );
};

export default OutgoingInvoicesPage;
